import React, { useCallback, useEffect, useState, useRef } from "react";
import Aux from "../../hoc/Auxiliary"
import PageDetails from "../../components/DataUpload/PageDetails/PageDetails";
import CategoriesData from "../../components/DataUpload/CategoriesData/CategoriesData";
import ActionsList from "../../components/DataUpload/ActionsList/ActionsList";
import HeaderBreadCrumb, {normalizePathItem} from "../../components/BreadCrumb/Breadcrumb";
import {
    findByDCAndMarketNode,
    getLevel5ValidationStatus,
    getWriteAccessMarketNodeDetails,
    persistData,
    publishData
} from "../../api/Category/category-service";
import runMarketNodeValidation from "../../api/MarketNode/marketnode-service";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {loadState, removeFromSessionStorage} from "../../api/State/manage-state-storage-service";
import {showError, showInfo, showSuccess, showTimedError, showWarn} from "../../api/notification/ToastManager";
import {
    isLevel4ButtonDisabled,
    isTemplatesInProgress,
    isValidationInProgress
} from "../../api/MarketNode/marketNode-status";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import {onServerCallError} from "../../api/utils/errors";
import {getAllQueryFileNamesPerDataCollection} from "../../api/file/upload-service";
import {StatusCode, StatusContext} from "../../entities/StatusContext";
import {updateMarketNodeStatus} from "../../api/dataCollection/dataCollection-service";
import {getCoordinations} from "../../api/Coordination/coordination-service";


const DataUploadBuilder = () => {
    const {state} = useLocation();
    const marketNode = loadState(state, "marketNode");
    const dataCollection = loadState(state, "dataCollection");
    const dcMarketNodeId = loadState(state, "dcMarketNodeId");
    const urlParams = useParams();
    const [categories, setCategories] = useState([]);
    const [statusLV4, setStatusLV4] = useState(false);
    const [btnPublishVisibility, setBtnPublishVisibility] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isMarketNodeDataPublishing, setIsMarketNodeDataPublishing] = useState(false);
    const persistingLabel = "Persisting Data ..."
    const [publishBtnLabel, setPublishBtnLabel] = useState("Publish Data");
    const [crumbs, setCrumbs] = useState(["Data Collections"])
    const navigate = useNavigate();
    const [validationButtonDisabled, setValidationButtonDisabled] = useState(false);
    const [refreshTime, setRefreshTime] = useState(120000);
    const [isCoordinationPopupVisible, setIsCoordinationPopupVisible] = useState(false);
    const [marketNodeData, setMarketNodeData] = useState(null);
    const [notifToFixErrors, setNotifToFixErrors] = useState(false);
    const [crossValidationMsg, setCrossValidationMsg] = useState("");
    const [uploadMethod, setUploadMethod] = useState("");
    const [isCoordinationDataCollection, setIsCoordinationDataCollection] = useState(false);
    const suppressNotificationRef = useRef(false);

    const handleDataReuploaded = useCallback(() => {
        suppressNotificationRef.current = true;
        setNotifToFixErrors(false);
    }, []);

    const updateRefreshTime = (data) => {
        if ((isValidationInProgress(data) || isTemplatesInProgress(data))
            && document.visibilityState === 'visible') {
            setRefreshTime(5000);
        } else {
            setRefreshTime(30000);
        }
    }

    useEffect(() => {
            const fetchMarketNode = async () => {
                const mz = await getWriteAccessMarketNodeDetails(dataCollection.id, marketNode.id)
                let level5Result = (await getLevel5ValidationStatus(dataCollection, marketNode));
                await findByDCAndMarketNode(dataCollection, marketNode)
                    .then(res => {
                        if (res.status === 200) {

                            setUploadMethod(res.content[0]['categoryDTO'].uploadMethod?.name)
                            setMarketNodeData({id: dcMarketNodeId, name: res.content[0].marketNodeDTO.name})
                            updateRefreshTime(res.content)
                            setCategories(res.content);
                            updateValidationButtonStatus(res.content)
                            updatePublishButtonStatus(res.content, level5Result)
                            setStatusLV4(updateLevel4Status(res.content));
                            setCrumbs(Array.from(new Set([...crumbs, dataCollection?.name, res.content[0]['marketNodeDTO'].name])))
                        } else {
                            onResponseError();
                        }
                    })
                    .catch(_ => {
                        onResponseError()
                    })
            }
            if (dataCollection && marketNode) {
                try {
                    fetchMarketNode().then(() => {
                        setIsLoading(false);
                    })
                } catch (e) {
                    onResponseError();
                }
            }

        }, []
    );

    const checkMarketNodeStatus = (marketNode) => {

        if (marketNode?.marketNodeStatus?.code === StatusCode.SUBMISSION_COMPLETED || marketNode?.marketNodeStatus?.code === StatusCode.SUBMISSION_COMPLETED_AND_PERSISTED) {
            showTimedError("Provide data is forbidden",
                "Access to providing data page is forbidden when market node is published or persisted.", 6000)
            navigate("/data_collections")
        }
    }

    const onResponseError = () => {
        onServerCallError("Error", "An error was occurred when searching categories data",
            () => {
                navigate(-1);
            })
    }


    useEffect(() => {
        if (urlParams?.dcName && urlParams.dcName.toLowerCase() !== normalizePathItem(dataCollection?.name)
            || urlParams?.marketNode && urlParams.marketNode.toLowerCase() !== normalizePathItem(marketNode?.name)) {
            removeFromSessionStorage("dataCollection")
            removeFromSessionStorage("marketNode")
            removeFromSessionStorage("dcMarketNodeId")
            navigate("/data_collections");
        }
    }, [])

    const updatePublishButtonStatus = (data, level5Result) => {
        setBtnPublishVisibility(false);
        if (level5Result.content.hasValidationLevel5) {
            var hasUpload = data.map(e => {
                if ((e['validationDTO'].status.context !== "LEVEL4_STEPS") || (e['validationDTO'].status.context === "LEVEL4_STEPS"
                    && (e['validationDTO'].status.code !== ("VALIDATION_SUCCESS") &&
                        e['validationDTO'].status.code !== StatusCode.FILES_SKIPPED))) {
                    return true;
                }
            })
            setBtnPublishVisibility(!level5Result.content.isValidationLevel5Valid && hasUpload.length > 0);
        } else {
            data.map(e => {
                if ((e['validationDTO'].status.context !== "LEVEL4_STEPS") || (e['validationDTO'].status.context === "LEVEL4_STEPS"
                    && (e['validationDTO'].status.code !== ("VALIDATION_SUCCESS") &&
                        e['validationDTO'].status.code !== StatusCode.FILES_SKIPPED))) {
                    setBtnPublishVisibility(true);
                }
            })
        }

    }
    const updateValidationButtonStatus = (data) => {
        setValidationButtonDisabled(isLevel4ButtonDisabled(data.map(e => e['validationDTO'])))
    }

    const publish = () => {
        if (categories.filter(x => x.validationDTO.status.code !== StatusCode.FILES_SKIPPED).length === 0) {
            updateMarketNodeStatus(dataCollection?.id, marketNode.id, StatusCode.FILES_SKIPPED, StatusContext.MARKET_NODE_STEPS).then(r => {
                if (r.status === 200) {
                    showSuccess("Success", `Files upload skipped successfully !`);
                } else {
                    showError("Error", "An error was occurred when skipping upload files")
                }
                findByDCAndMarketNode(dataCollection, marketNode).then(() => {
                    navigate("/data_collections/" + dataCollection?.name);
                })
            })

        } else {
            publishData(categories, dataCollection?.id, dataCollection?.name).then(_ => {
                findByDCAndMarketNode(dataCollection, marketNode)
                    .then(data => {
                        setIsMarketNodeDataPublishing(true);
                        if (data.status === 200) {
                            getAllQueryFileNamesPerDataCollection(dataCollection?.id).then(res => {
                                if (res.length === 0) {
                                    showWarn("Info",
                                        "Market node data could not be persisted due to missing configuration.Please contact your administrator",
                                        10000)
                                    navigate("/data_collections/" + dataCollection?.name);
                                } else {
                                    setPublishBtnLabel(persistingLabel)
                                    persistData(categories, dataCollection?.id, dataCollection?.name).then(r => {
                                        if (r.status === 200) {
                                            showSuccess("Success", `market node Data is persisted`);
                                        } else if (r.status === 504) {
                                            showInfo("Info", "Persisting market node might take several minutes")
                                        } else {
                                            showError("Error", "An error was occurred when persisting market node data")
                                        }
                                        navigate("/data_collections/" + dataCollection?.name);
                                    });
                                }

                            });

                        } else {
                            showError("Error", "An error was occurred when searching categories data")
                        }

                    })
            })
        }

    }

    async function findByDCAndMN() {
        let level5Result = await getLevel5ValidationStatus(dataCollection, marketNode);
        if (isCoordinationDataCollection === true) {
            updateUploadButtonVisibility()
        }
        findByDCAndMarketNode(dataCollection, marketNode)
            .then(res => {
                if (res.status === 200) {
                    var coordinationSubjectTopics = res.content.filter(c => (c['categoryDTO'].topics.filter(s => s.coordinationSubject === true))).length;
                    coordinationSubjectTopics > 0 ? setIsCoordinationDataCollection(true) : setIsCoordinationDataCollection(false);
                    updateRefreshTime(res.content)
                    updatePublishButtonStatus(res.content, level5Result);
                    updateValidationButtonStatus(res.content)
                    setCategories(res.content)
                    setStatusLV4(updateLevel4Status(res.content));
                }
            })
    }

    const validateAllData = useCallback(() => {
        setStatusLV4(true);
        runMarketNodeValidation(dataCollection, marketNode)
            .then(() => {
                findByDCAndMN();
            });
    }, [])
    const updateLevel4Status = (data) => {
        let init = false;
        data.map(e => {
            if (e['validationDTO'].status.code === "VALIDATION_IN_PROGRESS"
                && e['validationDTO'].status.context === "LEVEL4_STEPS") {
                init = true;
            }
        })
        return init;
    }
    useEffect(() => {
        if (dataCollection && marketNode) {
            findByDCAndMN()
        }
    }, []);
    useEffect(() => {
        if (dataCollection && marketNode) {
            setTimeout(() => {
                findByDCAndMN()
            }, refreshTime);
        }
    });

    async function updateUploadButtonVisibility() {
        if (marketNodeData !== null && marketNodeData !== undefined) {

            if (suppressNotificationRef.current) {
                setNotifToFixErrors(false);
                return;
            }

            const r = await getCoordinations(marketNodeData?.id);
            if (r.content.length > 0) {
                const message = r.content.some(s => s.status.code === "VALIDATION_IN_PROGRESS")
                    ? "Cross TSOs validation in progress " : "Cross files validation in progress ";
                setCrossValidationMsg(message);
                if (!r.content.some(s => s.status.code === "VALIDATION_IN_PROGRESS"))
                {
                    var requestsToFixError = r.content.filter(v => v.fixErrorsBy?.name === marketNode.name);
                    if (requestsToFixError.length > 0) {
                        console.log(requestsToFixError);
                        var requesters = requestsToFixError.map(x => x.fixErrorsBy.name===x.firstMarketNode.marketNode.name?
                            x.secondMarketNode.marketNode.name:x.firstMarketNode.marketNode.name);
                        console.log(requesters.join(", "))
                        setNotifToFixErrors({
                            "isNotifVisible": true,
                            "requesters":  requesters.join(", ")
                        })
                    } else {
                        setNotifToFixErrors(false);
                    }
                } else {
                    setNotifToFixErrors(false);
                }

            } else {
                setNotifToFixErrors(false);
            }
        }
    }

    useEffect(() => {
        if (isCoordinationDataCollection === true) {
            const intervalId = setInterval(() => {
                updateUploadButtonVisibility();
            }, 3000)
            return () => clearInterval(intervalId);
        }
    }, [marketNodeData]);

    let content = isLoading ?
        (
            <LoadingScreen/>
        ) :
        (<Aux>
            <HeaderBreadCrumb items={crumbs}/>
            <PageDetails dcName={dataCollection?.name}
                         marketNode={marketNode?.name}
                         dcMarketNodeId={dcMarketNodeId}
                         isIGM={uploadMethod==="zip-xml"}
            />
            <CategoriesData categoriesProps={categories}
                            dcMarketNodeId={dcMarketNodeId}
                            modelPath={dataCollection?.modelPath}
                            statusLV4={statusLV4}
                            crossValidationMsg={crossValidationMsg}
                            dataCollection={dataCollection}
                            setValidationButtonDisabled={setValidationButtonDisabled}
                            marketNodeDataIsPublishing={isMarketNodeDataPublishing}
                            findByDCAndMN={findByDCAndMN}
                            setPublishBtnLabel={setPublishBtnLabel}
                            setBtnPublishVisibility={setBtnPublishVisibility}
                            onDataReuploaded={handleDataReuploaded}
            />
            <ActionsList onPublishClick={publish} btnPublishVisibility={btnPublishVisibility} statusLV4={statusLV4}
                         onValidateAllDataClick={validateAllData}
                         marketNodeData={marketNodeData}
                         haveMultipleCategories={categories.length > 1}
                         isCoordinationPopupVisible={isCoordinationPopupVisible}
                         setIsCoordinationPopupVisible={setIsCoordinationPopupVisible}
                         notifToFixErrors={notifToFixErrors}
                         validationButtonDisabled={validationButtonDisabled}
                         publishBtnLabel={publishBtnLabel}
                         marketNodeDataIsPublishing={isMarketNodeDataPublishing}
                         persistingLabel={persistingLabel}
                         isCoordinationDataCollection={isCoordinationDataCollection}
                         dataCollection={dataCollection}
                        uploadMethod={uploadMethod}/>
        </Aux>)
    return <div>{content}</div>
};
export default DataUploadBuilder;
